import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom'; // To detect changes in the URL
import ToolbarPopover from './ToolbarPopover';
import { formatDate } from '../../../../utils/dateUtils';
import { useAuth } from '../../../../context/AuthContext';

// Date field names for special handling
const DATE_FIELDS = ['purchase_date', 'sale_date'];

// Ensure date is in YYYY-MM-DD format for filtering
const formatDateForFilter = (dateStr, user) => {
  if (!dateStr) return '';
  try {
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // If in MM/DD/YYYY format, convert to YYYY-MM-DD
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // For other formats, use Date object
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return ''; // Invalid date
    return formatDate(date, { user, format: 'YYYY-MM-DD' });
  } catch (e) {
    console.error('Error formatting date:', e);
    return '';
  }
};

// Parse date range string or array into array
const parseDateRange = (value, user) => {
  if (!value) return ['', ''];
  if (Array.isArray(value)) return value;
  const [start, end] = value.split(' to ');
  return [formatDateForFilter(start, user) || '', formatDateForFilter(end, user) || ''];
};

const FilterPopover = ({
  id,
  open,
  anchorEl,
  onClose,
  columns,
  filters = [],
  onFilterAdd,
  onFilterChange,
  onFilterValueChange,
  onFilterRemove,
  onFilterClear,
  getDefaultOperator,
  onLogicOperatorChange,
  filteredCount
}) => {
  const { user } = useAuth();
  const location = useLocation(); // so our effect can re-run when the URL changes
  const [newFilter, setNewFilter] = useState({
    column: '',
    operator: 'contains',
    value: '',
    logicOperator: null
  });

  // Parse OR expression from URL parameter
  const parseOrExpression = (value) => {
    if (!value) return [];
    if (value.startsWith('(') && value.endsWith(')')) {
      // Extract values between parentheses and split by OR
      return value.slice(1, -1).split(' OR ').map(v => v.trim());
    }
    return [value];
  };

  // Use ref to track if we've processed the current URL parameters
  const processedUrlRef = useRef('');

  // Handle URL parameters when they change
  useEffect(() => {
    const currentUrl = location.search;
    
    // Skip if we've already processed these exact URL parameters
    if (processedUrlRef.current === currentUrl) {
      return;
    }

    const handleUrlParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const params = {
        contact_name: urlParams.get('contact_name'),
        metal: urlParams.get('metal'),
        country: urlParams.get('country'),
        denomination: urlParams.get('denomination'),
        TPG: urlParams.get('TPG'),
        purchase_date: urlParams.get('purchase_date'),
        year: urlParams.get('year')
      };

      // Only proceed if we have any URL parameters
      if (Object.values(params).some(v => v !== null)) {
        // Clear existing filters first
        const currentFilters = [...filters];
        for (let i = currentFilters.length - 1; i >= 0; i--) {
          onFilterRemove?.(i);
        }

        const newFilters = [];
        
        // Process each parameter type
        if (params.year) {
          const values = parseOrExpression(params.year);
          newFilters.push(...values.map((v, i) => ({
            column: 'year',
            operator: 'equals',
            value: v,
            logicOperator: i < values.length - 1 ? 'OR' : null
          })));
        }

        if (params.country) {
          const values = parseOrExpression(params.country);
          newFilters.push(...values.map((v, i) => ({
            column: 'country',
            operator: 'equals',
            value: v,
            logicOperator: i < values.length - 1 ? 'OR' : null
          })));
        }

        if (params.purchase_date) {
          const rangeMatch = params.purchase_date.match(/\[(.*?)\s+TO\s+(.*?)\]/);
          if (rangeMatch) {
            const [_, startDate, endDate] = rangeMatch;
            newFilters.push({
              column: 'purchase_date',
              operator: 'between',
              value: `${formatDateForFilter(startDate, user)} to ${formatDateForFilter(endDate, user)}`,
              logicOperator: null
            });
          } else {
            const dateValue = formatDateForFilter(params.purchase_date.replace(/[\[\]]/g, ''), user);
            if (dateValue) {
              newFilters.push({
                column: 'purchase_date',
                operator: 'equals',
                value: dateValue,
                logicOperator: null
              });
            }
          }
        }

        // Add simple filters
        if (params.metal) {
          newFilters.push({
            column: 'metal',
            operator: 'equals',
            value: params.metal,
            logicOperator: null
          });
        }

        if (params.denomination) {
          newFilters.push({
            column: 'denomination',
            operator: 'equals',
            value: params.denomination,
            logicOperator: null
          });
        }

        if (params.TPG) {
          newFilters.push({
            column: 'certification_service_name',
            operator: params.TPG === 'isEmpty' ? 'isEmpty' : 'equals',
            value: params.TPG === 'isEmpty' ? '' : params.TPG,
            logicOperator: null
          });
        }

        if (params.contact_name) {
          newFilters.push({
            column: 'contact_name',
            operator: 'contains',
            value: params.contact_name,
            logicOperator: null
          });
        }

        // Add all filters at once
        if (newFilters.length > 0) {
          onFilterAdd?.(newFilters);
          // Mark these URL parameters as processed
          processedUrlRef.current = currentUrl;
        }
      }
    };

    handleUrlParams();
  }, [location.search, onFilterAdd, onFilterRemove]); // Watch for URL changes and callback changes

  // Clear URL parameters when manually editing filters
  const clearUrlParams = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('userId'); // preserve userId if needed

    // Remove filter‐related params
    urlParams.delete('metal');
    urlParams.delete('country');
    urlParams.delete('denomination');
    urlParams.delete('TPG');
    urlParams.delete('contact_name');
    urlParams.delete('purchase_date');
    urlParams.delete('year');
    if (userId) {
      urlParams.set('userId', userId);
    }
    window.history.replaceState(null, '', `?${urlParams.toString()}`);
  }, []);

  const handleAddNewFilter = useCallback((filterToAdd) => {
    if (filterToAdd.column && filterToAdd.operator) {
      // Don't require a value for isEmpty/isNotEmpty
      if (['isEmpty', 'isNotEmpty'].includes(filterToAdd.operator) || filterToAdd.value) {
        clearUrlParams();
        
        // Format date values
        let formattedFilter = { ...filterToAdd };
        if (DATE_FIELDS.includes(filterToAdd.column) && !['isEmpty', 'isNotEmpty'].includes(filterToAdd.operator)) {
          if (filterToAdd.operator === 'between') {
            // For between operator, ensure both dates are formatted
            const [startDate, endDate] = Array.isArray(filterToAdd.value) ? filterToAdd.value : filterToAdd.value.split(' to ');
            if (startDate && endDate) {
              formattedFilter.value = `${formatDateForFilter(startDate, user)} to ${formatDateForFilter(endDate, user)}`;
            } else {
              return; // Don't add incomplete date range
            }
          } else {
            // For single date operators (ON, before, after), format the date
            formattedFilter.value = formatDateForFilter(filterToAdd.value, user);
            // Add operator field for date filters
            formattedFilter[`${filterToAdd.column}_operator`] = filterToAdd.operator;
          }
        }
        
        onFilterAdd?.(formattedFilter);
        setNewFilter({
          column: '',
          operator: 'contains',
          value: '',
          logicOperator: null
        });
      }
    }
  }, [onFilterAdd, clearUrlParams]);

  const handleLogicOperatorChange = (filter, value, index) => {
    clearUrlParams();
    const updatedFilter = { ...filter, logicOperator: value };
    if (index !== undefined) {
      // Update existing filter
      onFilterChange?.(index, updatedFilter);
      onLogicOperatorChange?.(value);
      // Only show new row, don't add filter yet
      setNewFilter({
        column: '',
        operator: 'contains',
        value: '',
        logicOperator: null
      });
    } else {
      // New filter row
      handleAddNewFilter(updatedFilter);
    }
  };

  const handleColumnChange = (e) => {
    clearUrlParams();
    const column = e.target.value;
    setNewFilter(prev => ({
      ...prev,
      column,
      operator: getDefaultOperator?.(column) || (DATE_FIELDS.includes(column) ? 'equals' : 'contains')
    }));
  };

  const handleOperatorChange = (e) => {
    clearUrlParams();
    const newOperator = e.target.value;
    setNewFilter(prev => ({
      ...prev,
      operator: newOperator,
      // Reset value when switching to/from between operator
      value: newOperator === 'between' ? ['', ''] : 
        (Array.isArray(prev.value) ? '' : prev.value)
    }));
  };

  const handleValueChange = (e) => {
    clearUrlParams();
    const value = e.target.value;
    if (DATE_FIELDS.includes(newFilter.column)) {
      if (newFilter.operator === 'between') {
        setNewFilter(prev => ({
          ...prev,
          value: Array.isArray(prev.value) ? [value, prev.value[1]] : [value, '']
        }));
      } else {
        setNewFilter(prev => ({
          ...prev,
          value
        }));
      }
    } else {
      setNewFilter(prev => ({
        ...prev,
        value
      }));
    }
  };

  const handleSecondDateChange = (e) => {
    clearUrlParams();
    const value = e.target.value;
    setNewFilter(prev => ({
      ...prev,
      value: Array.isArray(prev.value) ? [prev.value[0], value] : ['', value]
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && newFilter.column && (newFilter.value || ['isEmpty', 'isNotEmpty'].includes(newFilter.operator))) {
      handleAddNewFilter(newFilter);
    }
  };

  return (
    <ToolbarPopover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      title="Filter"
      showCloseButton={true}
    >
      <div className="px-4 sm:px-6 pt-1 pb-4 w-full sm:w-[800px] transition-all duration-200 max-h-[80vh] overflow-y-auto">
        {/* Headers */}
        <div className="hidden sm:grid sm:grid-cols-[auto,3fr,2.5fr,3fr,auto] gap-6 mb-2 items-center min-h-[32px]">
          <div className="w-9"></div>
          <div className="text-sm font-medium text-gray-900">Columns</div>
          <div className="text-sm font-medium text-gray-900">Operator</div>
          <div className="text-sm font-medium text-gray-900">Value</div>
          <div className="w-[88px]"></div>
        </div>

        {/* Active Filters */}
        <div className={`space-y-4 sm:space-y-3 ${filters.length > 0 ? 'mb-3' : ''} transition-all duration-200`}>
          {filters.map((filter, index) => (
            <div
              key={index}
              className="flex flex-col sm:grid sm:grid-cols-[3fr,2.5fr,3fr,auto] gap-2 sm:gap-6 sm:items-center border-b border-gray-200 sm:border-none pb-4 sm:pb-0"
            >
              <div>
                <label className="sm:hidden text-sm font-medium text-gray-700 mb-1">Column</label>
                <select
                  value={filter.column}
                  onChange={(e) => {
                    clearUrlParams();
                    onFilterChange?.(index, { ...filter, column: e.target.value });
                  }}
                  className="block w-full pl-3 pr-12 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {[...columns]
                    .sort((a, b) => a.headerName.localeCompare(b.headerName))
                    .map(column => (
                      <option key={column.field} value={column.field}>
                        {column.headerName}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="sm:hidden text-sm font-medium text-gray-700 mb-1">Operator</label>
                <select
                  value={filter.operator}
                  onChange={(e) => {
                    clearUrlParams();
                    const newOperator = e.target.value;
                    // Reset value when switching to/from between operator
                    const newValue = newOperator === 'between' ? ['', ''] : 
                      (Array.isArray(filter.value) ? '' : filter.value);
                    onFilterChange?.(index, { 
                      ...filter, 
                      operator: newOperator,
                      value: newValue
                    });
                  }}
                  className="block w-full pl-3 pr-12 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {DATE_FIELDS.includes(filter.column) ? (
                    <>
                      <option value="equals">On</option>
                      <option value="lessThanOrEqual">Before</option>
                      <option value="greaterThanOrEqual">After</option>
                      <option value="between">Between</option>
                      <option value="isEmpty">Is empty</option>
                      <option value="isNotEmpty">Is not empty</option>
                    </>
                  ) : (
                    <>
                      <option value="contains">Contains</option>
                      <option value="equals">Equals</option>
                      <option value="isNot">Is not</option>
                      <option value="startsWith">Starts with</option>
                      <option value="endsWith">Ends with</option>
                      <option value="isEmpty">Is empty</option>
                      <option value="isNotEmpty">Is not empty</option>
                    </>
                  )}
                </select>
              </div>
              <div className="w-full">
                <label className="sm:hidden text-sm font-medium text-gray-700 mb-1">Value</label>
                <div className={`flex ${filter.operator === 'between' && DATE_FIELDS.includes(filter.column) ? 'flex-col' : ''} gap-2 items-center w-full`}>
                  {DATE_FIELDS.includes(filter.column) ? (
                    filter.operator === 'between' ? (
                      <>
                        <div className="flex items-center gap-2 w-full">
                          <label className="text-xs text-gray-500 w-8">From:</label>
                          <input
                            type="date"
                            value={parseDateRange(filter.value, user)[0]}
                            onChange={(e) => {
                              clearUrlParams();
                              const [_, endDate] = parseDateRange(filter.value, user);
                              const formattedStartDate = formatDateForFilter(e.target.value, user);
                              onFilterValueChange?.(index, `${formattedStartDate} to ${endDate}`);
                            }}
                            className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div className="flex items-center gap-2 w-full">
                          <label className="text-xs text-gray-500 w-8">To:</label>
                          <input
                            type="date"
                            value={parseDateRange(filter.value, user)[1]}
                            onChange={(e) => {
                              clearUrlParams();
                              const [startDate] = parseDateRange(filter.value, user);
                              const formattedEndDate = formatDateForFilter(e.target.value, user);
                              onFilterValueChange?.(index, `${startDate} to ${formattedEndDate}`);
                            }}
                            className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </>
                    ) : (
                      <input
                        type="date"
                        value={filter.value || ''}
                        onChange={(e) => {
                          clearUrlParams();
                          const formattedDate = formatDateForFilter(e.target.value, user);
                          const updatedFilter = {
                            ...filter,
                            value: formattedDate,
                            [`${filter.column}_operator`]: filter.operator
                          };
                          onFilterChange?.(index, updatedFilter);
                        }}
                        className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    )
                  ) : (
                  <input
                    type="text"
                    value={filter.value}
                    onChange={(e) => {
                      clearUrlParams();
                      onFilterValueChange?.(index, e.target.value);
                    }}
                    className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter value..."
                  />
                )}
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => handleLogicOperatorChange(filter, 'AND', index)}
                  className={`px-3 py-2 text-sm border rounded-md w-full sm:w-auto ${
                    filter.logicOperator === 'AND'
                      ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-300'
                  }`}
                >
                  AND
                </button>
                <button
                  onClick={() => handleLogicOperatorChange(filter, 'OR', index)}
                  className={`px-3 py-2 text-sm border rounded-md w-full sm:w-auto ${
                    filter.logicOperator === 'OR'
                      ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-300'
                  }`}
                >
                  OR
                </button>
                <button
                  onClick={() => {
                    clearUrlParams();
                    onFilterRemove?.(index);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-500"
                  aria-label="Remove filter"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M6 18L18 6M6 6l12 12" 
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* New Filter Row */}
        {(filters.length === 0 || filters[filters.length - 1].logicOperator) && (
          <div className="flex flex-col sm:grid sm:grid-cols-[auto,3fr,2.5fr,3fr,auto] gap-2 sm:gap-6 sm:items-center transition-all duration-200 pt-4 sm:pt-0">
            <div className="hidden sm:block">
              <button
                onClick={() => setNewFilter({
                  column: '',
                  operator: 'contains',
                  value: '',
                  logicOperator: null
                })}
                className="p-2 text-gray-400 hover:text-gray-500"
                aria-label="Remove filter"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
              </button>
            </div>
            <div>
              <label className="sm:hidden text-sm font-medium text-gray-700 mb-1">Column</label>
              <select
                value={newFilter.column}
                onChange={handleColumnChange}
                className="block w-full pl-3 pr-12 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select a column</option>
                {[...columns]
                  .sort((a, b) => a.headerName.localeCompare(b.headerName))
                  .map(column => (
                    <option key={column.field} value={column.field}>
                      {column.headerName}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="sm:hidden text-sm font-medium text-gray-700 mb-1">Operator</label>
              <select
                value={newFilter.operator}
                onChange={handleOperatorChange}
                className="block w-full pl-3 pr-12 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={!newFilter.column}
              >
                {DATE_FIELDS.includes(newFilter.column) ? (
                  <>
                    <option value="equals">On</option>
                    <option value="lessThanOrEqual">Before</option>
                    <option value="greaterThanOrEqual">After</option>
                    <option value="between">Between</option>
                    <option value="isEmpty">Is empty</option>
                    <option value="isNotEmpty">Is not empty</option>
                  </>
                ) : (
                  <>
                    <option value="contains">Contains</option>
                    <option value="equals">Equals</option>
                    <option value="isNot">Is not</option>
                    <option value="startsWith">Starts with</option>
                    <option value="endsWith">Ends with</option>
                    <option value="isEmpty">Is empty</option>
                    <option value="isNotEmpty">Is not empty</option>
                  </>
                )}
              </select>
            </div>
            <div className="w-full">
              <label className="sm:hidden text-sm font-medium text-gray-700 mb-1">Value</label>
              <div className={`flex ${newFilter.operator === 'between' && DATE_FIELDS.includes(newFilter.column) ? 'flex-col' : ''} gap-2 items-center w-full`}>
                {DATE_FIELDS.includes(newFilter.column) ? (
                  newFilter.operator === 'between' ? (
                    <>
                      <div className="flex items-center gap-2 w-full">
                        <label className="text-xs text-gray-500 w-8">From:</label>
                        <input
                          type="date"
                          value={Array.isArray(newFilter.value) ? newFilter.value[0] : ''}
                          onChange={handleValueChange}
                          onKeyDown={handleKeyDown}
                          className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          disabled={!newFilter.column}
                        />
                      </div>
                      <div className="flex items-center gap-2 w-full">
                        <label className="text-xs text-gray-500 w-8">To:</label>
                        <input
                          type="date"
                          value={Array.isArray(newFilter.value) ? newFilter.value[1] : ''}
                          onChange={handleSecondDateChange}
                          onKeyDown={handleKeyDown}
                          className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          disabled={!newFilter.column}
                        />
                      </div>
                    </>
                  ) : (
                    <input
                      type="date"
                      value={newFilter.value || ''}
                      onChange={handleValueChange}
                      onKeyDown={handleKeyDown}
                      className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={!newFilter.column}
                    />
                  )
                ) : (
                  <input
                    type="text"
                    value={newFilter.value}
                    onChange={handleValueChange}
                    onKeyDown={handleKeyDown}
                    className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter value..."
                    disabled={!newFilter.column}
                  />
                )}
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => handleLogicOperatorChange(newFilter, 'AND')}
                className={`px-3 py-2 text-sm border rounded-md w-full sm:w-auto ${
                  newFilter.logicOperator === 'AND'
                    ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-300'
                }`}
                disabled={!newFilter.column || (!newFilter.value && !['isEmpty','isNotEmpty'].includes(newFilter.operator))}
              >
                AND
              </button>
              <button
                onClick={() => handleLogicOperatorChange(newFilter, 'OR')}
                className={`px-3 py-2 text-sm border rounded-md w-full sm:w-auto ${
                  newFilter.logicOperator === 'OR'
                    ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-300'
                }`}
                disabled={!newFilter.column || (!newFilter.value && !['isEmpty','isNotEmpty'].includes(newFilter.operator))}
              >
                OR
              </button>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-6 gap-4 sm:gap-0">
          <div className="flex items-center justify-between sm:justify-start sm:gap-4">
            <button
              onClick={async () => {
                onFilterClear?.();
                clearUrlParams();
                onClose?.();
              }}
              className="inline-flex items-center text-sm text-red-600 hover:text-red-700"
            >
              <svg className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path 
                  fillRule="evenodd" 
                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" 
                  clipRule="evenodd" 
                />
              </svg>
              Remove All
            </button>
            <button
              onClick={() => {
                // Also allow "isEmpty" or "isNotEmpty" with no value:
                if (
                  newFilter.column && 
                  (newFilter.value || ['isEmpty','isNotEmpty'].includes(newFilter.operator))
                ) {
                  handleAddNewFilter(newFilter);
                }
              }}
              disabled={!newFilter.column || (!newFilter.value && !['isEmpty','isNotEmpty'].includes(newFilter.operator))}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Apply Filter
            </button>
          </div>
          {filteredCount !== undefined && (
            <span className="text-sm text-gray-600 text-center sm:text-right mt-2 sm:mt-0">
              Showing {filteredCount} result{filteredCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </ToolbarPopover>
  );
};

export default React.memo(FilterPopover);
